import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Coins, Check, Lock, Palette, Crosshair, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { useProfile } from "../context/ProfileContext";
import { api } from "../lib/api";
import { THEMES, TARGET_SKINS, AURAS } from "../data/gameData";
import { sound } from "../lib/sound";

const SLOTS = [
  { key: "theme", label: "World Themes", icon: Palette, defs: THEMES,
    preview: (id) => <div className="w-full h-14 rounded-xl" style={{ background: THEMES[id]?.bg, backgroundSize: "cover" }} /> },
  { key: "target", label: "Target Skins", icon: Crosshair, defs: TARGET_SKINS,
    preview: (id) => (
      <div className="w-full h-14 flex items-center justify-center">
        <div className="w-11 h-11 border-4 flex items-center justify-center font-math font-bold"
             style={{ borderColor: TARGET_SKINS[id]?.color, borderRadius: TARGET_SKINS[id]?.radius,
                      boxShadow: `0 0 16px ${TARGET_SKINS[id]?.color}88` }}>7</div>
      </div>
    ) },
  { key: "aura", label: "Coach Auras", icon: Sparkles, defs: AURAS,
    preview: (id) => (
      <div className="w-full h-14 flex items-center justify-center">
        <div className="w-11 h-11 rounded-full" style={{ boxShadow: `0 0 22px ${AURAS[id]?.glow}`,
             background: AURAS[id]?.ring?.startsWith("conic") ? AURAS[id].ring : `${AURAS[id]?.glow}` }} />
      </div>
    ) },
];

export default function Shop() {
  const { profile, refresh } = useProfile();
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.getShop().then((d) => setCatalog(d.catalog)).catch(() => {}); }, []);

  if (!profile || !catalog) return <AppShell><div className="text-center text-[#A8B2D1] py-10">Loading shop…</div></AppShell>;

  const owned = profile.owned || [];
  const equipped = profile.equipped || {};

  const buy = async (item) => {
    setBusy(item.id);
    try {
      await api.buyCosmetic(profile.id, item.id);
      sound.win();
      toast.success(`Unlocked ${item.name}!`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not buy");
    } finally { setBusy(null); }
  };

  const equip = async (item) => {
    setBusy(item.id);
    try {
      await api.equipCosmetic(profile.id, item.id);
      sound.click();
      toast.success(`Equipped ${item.name}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not equip");
    } finally { setBusy(null); }
  };

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-4xl font-black leading-none">Shop</h1>
          <p className="text-[#A8B2D1] text-sm mt-1">Spend coins on cosmetics — never on learning!</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full glass font-black text-lg"
             data-testid="shop-coins">
          <Coins size={20} className="text-[#FFD600]" /> {profile.coins}
        </div>
      </div>

      {SLOTS.map((slot) => {
        const items = catalog[slot.key] || [];
        const Icon = slot.icon;
        return (
          <div key={slot.key} className="mb-7">
            <div className="flex items-center gap-2 mb-3">
              <Icon size={18} className="text-[#FF2A7A]" />
              <h2 className="text-lg font-bold">{slot.label}</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {items.map((item, i) => {
                const isOwned = owned.includes(item.id);
                const isEquipped = equipped[slot.key] === item.id;
                return (
                  <motion.div
                    key={item.id}
                    data-testid={`shop-item-${item.id}`}
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 * i }}
                    className="glass rounded-2xl p-3 border-2"
                    style={{ borderColor: isEquipped ? "#00FF66" : "rgba(255,255,255,0.12)" }}>
                    {slot.preview(item.id)}
                    <div className="font-bold text-sm mt-2 leading-tight">{item.name}</div>
                    {isEquipped ? (
                      <div className="mt-2 py-2 rounded-xl text-xs font-bold text-[#00FF66] flex items-center justify-center gap-1 bg-[#00FF66]/12">
                        <Check size={14} /> Equipped
                      </div>
                    ) : isOwned ? (
                      <button data-testid={`equip-${item.id}`} disabled={busy === item.id} onClick={() => equip(item)}
                        className="mt-2 w-full press glass rounded-xl py-2 text-xs font-bold">Equip</button>
                    ) : (
                      <button data-testid={`buy-${item.id}`} disabled={busy === item.id || profile.coins < item.price}
                        onClick={() => buy(item)}
                        className="mt-2 w-full press btn-3d rounded-xl py-2 text-xs font-bold text-white flex items-center justify-center gap-1 disabled:opacity-40"
                        style={{ background: "#FF2A7A" }}>
                        {profile.coins < item.price ? <Lock size={13} /> : <Coins size={13} />} {item.price}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
        );
      })}
    </AppShell>
  );
}
